import { motion } from "framer-motion";
import {
  FaExternalLinkAlt,
  FaMicrophone,
  FaRobot,
  FaChartLine,
  FaBolt,
  FaPlay,
  FaComments,
  FaRandom,
  FaChild,
  FaUserTie,
} from "react-icons/fa";
import { useState, useEffect, useRef } from "react";
import React from "react";

/* ─── AI Product Styles ─── */
const S = {
  section: {
    padding: "120px 24px",
    background: "#ffffff",
    position: "relative",
    overflow: "hidden",
  },
  bgGrid: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(18,175,9,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(18,175,9,.04) 1px,transparent 1px)",
    backgroundSize: "48px 48px",
    pointerEvents: "none",
  },
  container: {
    maxWidth: 1240,
    margin: "0 auto",
    position: "relative",
    zIndex: 2,
  },
  tagRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 20 },
  tagLine: {
    width: 40,
    height: 2,
    background: "linear-gradient(90deg,#12AF09,transparent)",
  },
  tag: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "2.5px",
    color: "#12AF09",
    textTransform: "uppercase",
  },
  titleLight: {
    display: "block",
    fontSize: 52,
    fontWeight: 300,
    color: "#111",
    lineHeight: 1.1,
  },
  titleBold: {
    display: "block",
    fontSize: 64,
    fontWeight: 900,
    background: "linear-gradient(135deg,#111 30%,#12AF09 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    lineHeight: 1,
    marginTop: 4,
  },
  card: {
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr",
    gap: 0,
    borderRadius: 40,
    overflow: "hidden",
    border: "1px solid rgba(18,175,9,.12)",
    boxShadow: "0 40px 80px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04)",
    marginBottom: 60,
    minHeight: 560,
    width: "100%",
    flexShrink: 0,
  },
  orbPanel: {
    background: "linear-gradient(160deg,#0a1a08 0%,#0f2b0c 50%,#0d1f0b 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    minHeight: 500,
  },
  // Social Media Panel - Updated with green theme
  socialPanel: {
    background: "linear-gradient(160deg,#0a1a08 0%,#0f2b0c 50%,#0d1f0b 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    minHeight: 500,
    padding: "30px",
  },
  infoPanel: {
    padding: "52px 48px",
    background: "#fcfcfc",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 0,
  },
  heroTitle: {
    fontSize: 38,
    fontWeight: 800,
    color: "#0f0f0f",
    lineHeight: 1.15,
    marginBottom: 8,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 14px",
    background: "rgba(18,175,9,.08)",
    border: "1px solid rgba(18,175,9,.18)",
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 700,
    color: "#12AF09",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    marginBottom: 20,
    width: "fit-content",
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#12AF09",
    display: "inline-block",
    boxShadow: "0 0 6px #12AF09",
  },
  desc: {
    fontSize: 15,
    lineHeight: 1.75,
    color: "#555",
    marginBottom: 36,
    paddingBottom: 32,
    borderBottom: "1px solid rgba(0,0,0,.06)",
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
    marginBottom: 36,
  },
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "14px 16px",
    background: "#fff",
    borderRadius: 18,
    border: "1px solid rgba(0,0,0,.05)",
    cursor: "default",
    transition: "all .25s",
  },
  featureIcon: {
    width: 38,
    height: 38,
    background: "rgba(18,175,9,.09)",
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#12AF09",
    fontSize: 16,
    flexShrink: 0,
    transition: "all .25s",
  },
  featureH4: {
    fontSize: 13,
    fontWeight: 700,
    color: "#111",
    marginBottom: 2,
  },
  featureP: { fontSize: 11, color: "#999", lineHeight: 1.4 },
  actions: { display: "flex", gap: 14, flexWrap: "wrap" },
  btnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 28px",
    background: "#111",
    color: "#fff",
    textDecoration: "none",
    borderRadius: 99,
    fontSize: 13,
    fontWeight: 700,
    border: "none",
    cursor: "pointer",
    transition: "all .3s",
  },
  btnDemo: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 28px",
    background: "linear-gradient(135deg,#12AF09,#0a7a05)",
    color: "#fff",
    border: "none",
    borderRadius: 99,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all .3s",
  },
  statsStrip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "28px 44px",
    background: "#f7f9f7",
    border: "1px solid rgba(18,175,9,.1)",
    borderRadius: 99,
    gap: 20,
    flexWrap: "wrap",
  },
  statItem: { textAlign: "center" },
  statNum: {
    display: "block",
    fontSize: 30,
    fontWeight: 900,
    color: "#12AF09",
    lineHeight: 1,
    marginBottom: 5,
  },
  statLabel: {
    fontSize: 11,
    color: "#999",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
  statDivider: { width: 1, height: 40, background: "#e0e0e0" },
  carouselWrapper: {
    position: "relative",
    overflow: "hidden",
    width: "100%",
  },
  carouselTrack: {
    display: "flex",
    gap: 30,
    transition: "transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)",
  },
  dotsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: 10,
    marginTop: 20,
    marginBottom: 30,
  },

  activeDot: {
    background: "#12AF09",
    width: 30,
    borderRadius: 99,
  },
};

/* ─── AI Orb Canvas ─── */
function AIOrb({ isPlaying }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const particlesRef = useRef([]);
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = (canvas.width = canvas.offsetWidth);
    const H = (canvas.height = canvas.offsetHeight);
    const cx = W / 2,
      cy = H / 2;

    particlesRef.current = Array.from({ length: 80 }, (_, i) => ({
      ring: i % 4,
      angle: Math.random() * Math.PI * 2,
      speed: 0.002 + Math.random() * 0.003,
      r: [80, 120, 165, 210][i % 4] + (Math.random() - 0.5) * 18,
      size: 1.2 + Math.random() * 2,
      opacity: 0.3 + Math.random() * 0.7,
      pulse: Math.random() * Math.PI * 2,
    }));

    function draw() {
      timeRef.current += isPlaying ? 0.022 : 0.008;
      const t = timeRef.current;
      ctx.clearRect(0, 0, W, H);

      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 220);
      glow.addColorStop(0, `rgba(18,175,9,${isPlaying ? 0.15 : 0.07})`);
      glow.addColorStop(1, "transparent");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, W, H);

      [80, 120, 165, 210].forEach((rad, ri) => {
        ctx.beginPath();
        ctx.arc(cx, cy, rad, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(18,175,9,${isPlaying ? 0.18 : 0.08})`;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 8]);
        ctx.lineDashOffset = -t * 30 * (ri % 2 === 0 ? 1 : -1);
        ctx.stroke();
        ctx.setLineDash([]);
      });

      particlesRef.current.forEach((p) => {
        p.angle += p.speed * (isPlaying ? 2.5 : 1);
        p.pulse += 0.04;
        const x = cx + Math.cos(p.angle) * p.r;
        const y = cy + Math.sin(p.angle) * p.r;
        const alpha = p.opacity * (0.6 + 0.4 * Math.sin(p.pulse));
        ctx.beginPath();
        ctx.arc(x, y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(18,175,9,${alpha})`;
        ctx.fill();
      });

      const pts = particlesRef.current.map((p) => ({
        x: cx + Math.cos(p.angle) * p.r,
        y: cy + Math.sin(p.angle) * p.r,
      }));
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 55) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(18,175,9,${(1 - dist / 55) * (isPlaying ? 0.55 : 0.2)})`;
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }
      }

      const orbSize = isPlaying ? 42 + Math.sin(t * 4) * 5 : 38;
      const coreGrad = ctx.createRadialGradient(
        cx - 8,
        cy - 8,
        2,
        cx,
        cy,
        orbSize,
      );
      coreGrad.addColorStop(0, "#7dffab");
      coreGrad.addColorStop(0.35, "#12AF09");
      coreGrad.addColorStop(1, "#065404");
      ctx.beginPath();
      ctx.arc(cx, cy, orbSize, 0, Math.PI * 2);
      ctx.fillStyle = coreGrad;
      ctx.shadowColor = "#12AF09";
      ctx.shadowBlur = isPlaying ? 40 : 20;
      ctx.fill();
      ctx.shadowBlur = 0;

      const shimmer = ctx.createRadialGradient(
        cx - 12,
        cy - 12,
        0,
        cx,
        cy,
        orbSize * 0.8,
      );
      shimmer.addColorStop(0, "rgba(255,255,255,.35)");
      shimmer.addColorStop(1, "transparent");
      ctx.beginPath();
      ctx.arc(cx, cy, orbSize, 0, Math.PI * 2);
      ctx.fillStyle = shimmer;
      ctx.fill();

      if (isPlaying) {
        [1, 2, 3].forEach((n) => {
          const progress = (t * 0.7 + n * 0.33) % 1;
          const pr = orbSize + progress * 90;
          ctx.beginPath();
          ctx.arc(cx, cy, pr, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(18,175,9,${(1 - progress) * 0.5})`;
          ctx.lineWidth = 1.5;
          ctx.stroke();
        });
      }

      animRef.current = requestAnimationFrame(draw);
    }

    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [isPlaying]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: "100%", height: "100%", display: "block" }}
    />
  );
}

/* ─── Social Media Image Gallery ─── */
function SocialMediaScreenshots() {
  const screenshots = [
    {
      id: 1,
      url: "./img/home.jpeg",
      title: "Home Feed",
      subtitle: "Latest posts & updates",
      color: "#12AF09",
    },
    {
      id: 2,
      url: "./img/chat.jpeg",
      title: "Chat Interface",
      subtitle: "Real-time messaging",
      color: "#12AF09",
    },
    {
      id: 3,
      url: "./img/profile.jpeg",
      title: "User Profile",
      subtitle: "Your social identity",
      color: "#12AF09",
    },
    {
      id: 4,
      url: "./img/random-chatting.jpeg",
      title: "Random Chat",
      subtitle: "Connect with new people",
      color: "#12AF09",
    },
    {
      id: 5,
      url: "./img/notification.jpeg",
      title: "Notifications",
      subtitle: "Real-time alerts",
      color: "#12AF09",
    },
  ];

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 16,
        padding: "4px",
      }}
    >
      {/* Main Featured Image */}
      <div
        style={{
          width: "100%",
          maxWidth: 500,
          borderRadius: 20,
          overflow: "hidden",
          boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
          position: "relative",
        }}
      >
        <img
          src={screenshots[0].url}
          alt={screenshots[0].title}
          style={{
            width: "100%",
            height: 300,
            objectFit: "contain",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "16px",
            background: "linear-gradient(transparent, rgba(0, 0, 0, 0.4))",
            color: "#fff",
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {screenshots[0].title}
          </div>
          <div style={{ fontSize: 13, opacity: 0.8 }}>
            {screenshots[0].subtitle}
          </div>
        </div>
      </div>

      {/* Thumbnail Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 10,
          width: "100%",
          maxWidth: 500,
        }}
      >
        {screenshots.slice(1).map((screenshot) => (
          <div
            key={screenshot.id}
            style={{
              borderRadius: 14,
              overflow: "hidden",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              cursor: "pointer",
              position: "relative",
              aspectRatio: "9/16",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
              e.currentTarget.style.boxShadow = "0 6px 25px rgba(0,0,0,0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
            }}
          >
            <img
              src={screenshot.url}
              alt={screenshot.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                padding: "6px 8px",
                background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                color: "#fff",
                fontSize: 8,
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              {screenshot.title}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Product data ─── */
const products = [
  // Social Media Product - Now First
  {
    id: "social",
    type: "social",
    title: "AINRAL",
    subtitle: "A New Way to Connect",
    description:
      "AINRAL reimagines the way we connect. With unique features like Child Profiles, and specialized verification badges for Government Officials, AINRAL is built for everyone. Private. Secure. Real. Yours.",
    features: [
      {
        icon: <FaRandom />,
        title: "Random Chatting",
        sub: "Connect with strangers & make new friends instantly",
      },
      {
        icon: <FaChild />,
        title: "Child Profile",
        sub: "Safe & secure profiles for children with parental controls",
      },
      {
        icon: <FaUserTie />,
        title: "Government Verified",
        sub: "Official verification badge for government officials",
      },
      {
        icon: <FaComments />,
        title: "Real-time Chat",
        sub: "Send messages, photos & videos instantly",
      },
    ],
    stats: [
      { num: "626+", label: "Beta Users" },
      { num: "1,132+", label: "Following" },
      { num: "Sept 9", label: "Launch Date" },
      { num: "2026", label: "Coming Soon" },
    ],
    badge: "Coming Soon • Sept 9, 2026",
    panelStyle: S.socialPanel,
    visualization: SocialMediaScreenshots,
    accentColor: "#12AF09",
    version: "v1.0",
  },
  // AI Calling Product - Now Second
  {
    id: "voice",
    type: "voice",
    title: "AI Calling",
    subtitle: "Cloud Platform",
    description:
      "AI-powered voice agents that automate calls, handle customer support, and manage business communication with human-like intelligence.",
    features: [
      {
        icon: <FaRobot />,
        title: "AI Voice Agents",
        sub: "Natural conversations 24/7",
      },
      {
        icon: <FaMicrophone />,
        title: "Call Automation",
        sub: "Smart routing & handling",
      },
      {
        icon: <FaBolt />,
        title: "Smart Workflows",
        sub: "Dynamic conversation flows",
      },
      {
        icon: <FaChartLine />,
        title: "Cloud Dashboard",
        sub: "Real-time analytics",
      },
    ],
    stats: [
      { num: "1K+", label: "Daily calls processed" },
      { num: "99.9%", label: "Uptime SLA" },
      { num: "24/7", label: "AI availability" },
      { num: "4+", label: "Upcoming products" },
    ],
    badge: "Live Product",
    panelStyle: S.orbPanel,
    visualization: AIOrb,
    accentColor: "#12AF09",
    version: "v2.0",
  },
];

/* ─── Product Card Component ─── */
function ProductCard({
  product,
  isPlaying,
  onDemo,
  hoveredFeature,
  setHoveredFeature,
  cardAnim,
}) {
  const isSocial = product.type === "social";
  const accentColor = product.accentColor;
  const Visualization = product.visualization;

  return (
    <motion.div
      variants={cardAnim}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      style={S.card}
      className="responsive-card"
    >
      {/* LEFT – Visualization Panel */}
      <div style={product.panelStyle} className="responsive-orb-panel">
        <Visualization isPlaying={isPlaying} />

        <div
          style={{
            position: "absolute",
            top: 24,
            left: 24,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "2px",
            color: isSocial ? "rgba(18,175,9,.5)" : "rgba(18,175,9,.5)",
            textTransform: "uppercase",
            background: isSocial ? "rgba(0,0,0,.3)" : "transparent",
            padding: isSocial ? "4px 12px" : "0",
            borderRadius: isSocial ? "20px" : "0",
            backdropFilter: isSocial ? "blur(10px)" : "none",
          }}
          className="version-label"
        >
          {isSocial
            ? "📱 " + product.title + " " + product.version
            : "AI Calling // " + product.version}
        </div>

        {isPlaying && !isSocial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              position: "absolute",
              bottom: 80,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "flex-end",
              gap: 3,
              height: 36,
            }}
            className="frequency-bars"
          >
            {Array.from({ length: 22 }).map((_, i) => (
              <motion.div
                key={i}
                animate={{ height: [8, 12 + Math.random() * 24, 8] }}
                transition={{
                  duration: 0.3 + Math.random() * 0.2,
                  repeat: Infinity,
                  delay: i * 0.04,
                  ease: "easeInOut",
                }}
                style={{
                  width: 3,
                  background: "linear-gradient(180deg,#12AF09,#0a7a05)",
                  borderRadius: 99,
                  minHeight: 4,
                }}
              />
            ))}
          </motion.div>
        )}
      </div>

      {/* RIGHT – Info Panel */}
      <div style={S.infoPanel} className="responsive-info-panel">
        <div
          style={{
            ...S.badge,
            background: "rgba(18,175,9,.08)",
            borderColor: "rgba(18,175,9,.18)",
            color: "#12AF09",
          }}
        >
          <span
            style={{
              ...S.dot,
              background: "#12AF09",
              boxShadow: "0 0 6px #12AF09",
            }}
          />
          {product.badge}
        </div>

        <h3 style={S.heroTitle} className="responsive-hero-title">
          {product.title}{" "}
          <span
            style={{
              background: "linear-gradient(135deg,#12AF09,#08a306)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {product.subtitle}
          </span>
        </h3>

        <p style={S.desc} className="responsive-desc">
          {product.description}
        </p>

        {/* Features */}
        <div style={S.featureGrid} className="responsive-feature-grid">
          {product.features.map((f, i) => {
            const isHovered = hoveredFeature === i + (isSocial ? 10 : 0);
            const featureIndex = i + (isSocial ? 10 : 0);

            return (
              <motion.div
                key={i}
                style={{
                  ...S.featureItem,
                  borderColor: isHovered
                    ? `rgba(18,175,9,.25)`
                    : "rgba(0,0,0,.05)",
                  boxShadow: isHovered
                    ? "0 8px 24px rgba(18,175,9,.08)"
                    : "none",
                  transform: isHovered ? "translateY(-2px)" : "translateY(0)",
                }}
                onMouseEnter={() => setHoveredFeature(featureIndex)}
                onMouseLeave={() => setHoveredFeature(null)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="responsive-feature-item"
              >
                <div
                  style={{
                    ...S.featureIcon,
                    background: isHovered ? accentColor : `rgba(18,175,9,.09)`,
                    color: isHovered ? "#fff" : "#12AF09",
                  }}
                >
                  {f.icon}
                </div>
                <div>
                  <div style={S.featureH4}>{f.title}</div>
                  <div style={S.featureP}>{f.sub}</div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Buttons */}
        <div style={S.actions} className="responsive-actions">
          <motion.a
            href="#"
            style={{
              ...S.btnPrimary,
              background: "#111",
              cursor: isSocial ? "not-allowed" : "pointer",
            }}
            whileHover={
              isSocial
                ? {}
                : {
                    background: accentColor,
                    gap: 14,
                    y: -2,
                    boxShadow: `0 12px 28px ${accentColor}40`,
                  }
            }
            whileTap={isSocial ? {} : { scale: 0.97 }}
            onClick={(e) => {
              if (isSocial) e.preventDefault();
            }}
            className={`responsive-btn ${isSocial ? "cursor-not-allowed" : ""}`}
          >
            <span>Visit Website</span>
            <FaExternalLinkAlt style={{ fontSize: 11 }} />
          </motion.a>

          <motion.button
            style={{
              ...S.btnDemo,
              background: "linear-gradient(135deg,#12AF09,#0a7a05)",
              opacity: isPlaying ? 0.75 : 1,
              cursor: isPlaying ? "not-allowed" : "pointer",
            }}
            onClick={() => onDemo(product.id)}
            disabled={isPlaying}
            whileHover={
              !isPlaying
                ? { y: -2, boxShadow: `0 12px 28px ${accentColor}40` }
                : {}
            }
            whileTap={!isPlaying ? { scale: 0.97 } : {}}
            animate={
              isPlaying
                ? {
                    boxShadow: [
                      `0 0 0 0 ${accentColor}60`,
                      `0 0 0 10px ${accentColor}00`,
                      `0 0 0 0 ${accentColor}60`,
                    ],
                  }
                : {}
            }
            transition={isPlaying ? { duration: 1.2, repeat: Infinity } : {}}
            className="responsive-btn"
          >
            <FaPlay
              style={{
                fontSize: 11,
                ...(isPlaying ? { animation: "spin .8s linear infinite" } : {}),
              }}
            />
            <span>{isPlaying ? "Demo Playing…" : "Watch Demo"}</span>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Main Component ─── */
const HomeProductsSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoSliding, setIsAutoSliding] = useState(true);
  const autoSlideTimerRef = useRef(null);

  const handleDemo = (productId) => {
    if (!isPlaying) {
      setIsPlaying(true);
      setTimeout(() => setIsPlaying(false), 6000);
    }
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoSliding(true);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % products.length);
  };

  useEffect(() => {
    if (isAutoSliding) {
      autoSlideTimerRef.current = setInterval(nextSlide, 5000);
    }
    return () => {
      if (autoSlideTimerRef.current) {
        clearInterval(autoSlideTimerRef.current);
      }
    };
  }, [isAutoSliding]);

  const handleMouseEnter = () => {
    setIsAutoSliding(false);
    if (autoSlideTimerRef.current) {
      clearInterval(autoSlideTimerRef.current);
    }
  };

  const handleMouseLeave = () => {
    setIsAutoSliding(true);
  };

  const cardAnim = {
    hidden: { opacity: 0, y: 40 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <>
      <section style={S.section} className="responsive-section">
        <div style={S.bgGrid} />

        <div style={S.container}>
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.12 } },
            }}
            style={{ marginBottom: 56 }}
          >
            <motion.div variants={cardAnim} style={S.tagRow}>
              <span style={S.tagLine} />
              <span style={S.tag}>Featured Innovations</span>
            </motion.div>
            <motion.h2 variants={cardAnim} style={{ margin: 0 }}>
              <span style={S.titleLight}>Explore our</span>
              <span style={S.titleBold}>Product Suite</span>
            </motion.h2>
          </motion.div>

          <div
            style={S.carouselWrapper}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div
              style={{
                ...S.carouselTrack,
                transform: `translateX(-${currentIndex * (100 + 30 / (1240 / 100))}%)`,
              }}
            >
              {products.map((product) => (
                <div key={product.id} style={{ minWidth: "100%" }}>
                  <ProductCard
                    product={product}
                    isPlaying={isPlaying && product.type === "voice"}
                    onDemo={handleDemo}
                    hoveredFeature={hoveredFeature}
                    setHoveredFeature={setHoveredFeature}
                    cardAnim={cardAnim}
                  />
                </div>
              ))}
            </div>
          </div>

          <div style={S.dotsContainer}>
            {products.map((_, index) => (
              <button
                key={index}
                style={{
                  ...S.dot,
                  ...(currentIndex === index ? S.activeDot : {}),
                }}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={S.statsStrip}
            className="responsive-stats"
          >
            {products[currentIndex].stats.map((s, i) => (
              <React.Fragment key={i}>
                <div style={S.statItem}>
                  <motion.span
                    style={S.statNum}
                    initial={{ opacity: 0, scale: 0.7 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: i * 0.1,
                      duration: 0.5,
                      type: "spring",
                    }}
                  >
                    {s.num}
                  </motion.span>
                  <span style={S.statLabel}>{s.label}</span>
                </div>
                {i < products[currentIndex].stats.length - 1 && (
                  <div
                    key={`div-${i}`}
                    style={S.statDivider}
                    className="responsive-divider"
                  />
                )}
              </React.Fragment>
            ))}
          </motion.div>
        </div>
      </section>

      <style>{`
        @keyframes spin { 
          to { transform: rotate(360deg); } 
        }
        
        @media (max-width: 1024px) {
          .responsive-card {
            grid-template-columns: 1fr 1.2fr !important;
          }
        }
        
        @media (max-width: 768px) {
          .responsive-section {
            padding: 80px 20px !important;
          }
          
          .responsive-card {
            grid-template-columns: 1fr !important;
            border-radius: 32px !important;
            margin-bottom: 48px !important;
          }
          
          .responsive-orb-panel {
            min-height: 420px !important;
          }
          
          .responsive-info-panel {
            padding: 36px 28px !important;
          }
          
          .responsive-hero-title {
            font-size: 30px !important;
          }
          
          .responsive-stats {
            padding: 24px 32px !important;
            border-radius: 60px !important;
            justify-content: center !important;
          }
          
          .responsive-divider {
            display: none !important;
          }
        }
        
        @media (max-width: 640px) {
          .responsive-feature-grid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
        }
        
        @media (max-width: 480px) {
          .responsive-section {
            padding: 60px 16px !important;
          }
          
          .responsive-orb-panel {
            min-height: 360px !important;
          }
          
          .responsive-info-panel {
            padding: 28px 20px !important;
          }
          
          .responsive-hero-title {
            font-size: 26px !important;
          }
          
          .responsive-desc {
            font-size: 14px !important;
            margin-bottom: 28px !important;
            padding-bottom: 24px !important;
          }
          
          .responsive-stats {
            padding: 20px 24px !important;
            border-radius: 48px !important;
            gap: 16px !important;
          }
          
          .responsive-actions {
            gap: 12px !important;
          }
          
          .responsive-btn {
            padding: 12px 24px !important;
            font-size: 12px !important;
          }
          
          .frequency-bars {
            bottom: 60px !important;
            gap: 2px !important;
            height: 28px !important;
          }
          
          .frequency-bars > div {
            width: 2px !important;
          }
          
          .version-label {
            font-size: 8px !important;
            top: 16px !important;
            left: 16px !important;
          }
        }
        
        @media (max-width: 768px) {
          [style*="font-size: 52px"] {
            font-size: 36px !important;
          }
          [style*="font-size: 64px"] {
            font-size: 44px !important;
          }
        }
        
        @media (max-width: 480px) {
          [style*="font-size: 52px"] {
            font-size: 28px !important;
          }
          [style*="font-size: 64px"] {
            font-size: 32px !important;
          }
          [style*="font-size: 38px"] {
            font-size: 26px !important;
          }
          [style*="font-size: 15px"] {
            font-size: 14px !important;
          }
          [style*="font-size: 30px"] {
            font-size: 24px !important;
          }
        }
      `}</style>
    </>
  );
};

export default HomeProductsSection;
